/**
 * Accessibility fixes for Syncfusion MultiSelect editable comboboxes.
 *
 * Syncfusion renders a hidden original <input> (which receives aria-labelledby /
 * aria-describedby props) and a separate VISIBLE combobox input
 * (`input.e-dropdownbase[role="combobox"]`) that the screen reader actually
 * interacts with. That visible input only gets aria-label="multiselect" and an
 * aria-describedby pointing at the internal chip collection.
 *
 * On focus we keep an sr-only selection summary in aria-describedby so landing
 * on the field still reads current values. Selection *changes* are announced
 * through a separate assertive live region that is NOT in aria-describedby —
 * JAWS in combobox/forms mode ignores polite live regions and describedby is
 * only spoken on focus.
 *
 * Combobox ARIA rewrites and chip-close button promotion are deferred on
 * change so they do not interrupt Syncfusion's native aria-selected speech.
 *
 * Chip remove / clear-all controls are rendered as unlabeled <span>s with
 * aria-hidden="true". We promote those to <button type="button"> with
 * aria-label and re-wire Syncfusion's mousedown handlers via ej2_instances.
 * Those buttons stay out of the Tab order (tabindex="-1"); selected chips are
 * reached with Left/Right arrows once the combobox has focus, matching
 * Syncfusion's native chip navigation.
 *
 * Syncfusion also owns its own assertive-adjacent live region
 * (`.e-chip-announcer`, aria-live="polite") that it uses to speak a generic
 * "Use arrow keys to open popup..." instruction every time the field is
 * focused, via `instance.announceInitialFocusInstruction()`. That message
 * never mentions how many items are currently selected or what they are,
 * even though Syncfusion's own add/remove-chip announcements do include
 * that count. We patch that one instance method (once per widget instance)
 * so the same live region also states the current selection on focus.
 */
export function getSyncfusionMultiSelectA11yHandlers({
  inputId,
  labelId,
  instructionsId,
  listboxLabel,
  fieldLabel,
  onChange,
} = {}) {
  let chipObserver = null;
  let describedByObserver = null;
  let clearObserver = null;
  let isPopupOpen = false;
  let previousLabels = [];

  const getOriginalInput = () => (inputId ? document.getElementById(inputId) : null);

  /**
   * Syncfusion's hidden original <input> ALSO carries the `.e-multiselect`
   * class (e.g. class="e-control e-multiselect e-lib"), so
   * `original.closest('.e-multiselect')` matches the input itself instead of
   * climbing to the real wrapper <div> that contains the chips/combobox.
   * Walk up starting at the parent so the original element can't self-match.
   */
  const getWrapper = () => {
    const original = getOriginalInput();
    let node = original && original.parentElement;
    while (node) {
      if (node.classList && node.classList.contains('e-multiselect')) return node;
      node = node.parentElement;
    }
    return null;
  };

  const getInstance = () => {
    const original = getOriginalInput();
    const instances = original && original.ej2_instances;
    return instances && instances[0] ? instances[0] : null;
  };

  const getCombobox = () => {
    const wrapper = getWrapper();
    if (!wrapper) return null;
    return wrapper.querySelector('input.e-dropdownbase, input[role="combobox"]');
  };

  const getSelectionSummaryId = () => (inputId ? `${inputId}-selection-summary` : null);

  const getLiveRegionId = () => (inputId ? `${inputId}-selection-live` : null);

  const getChipLabel = (chip) => {
    if (!chip) return '';
    const title = (chip.getAttribute('title') || '').trim();
    if (title) return title;
    const content = chip.querySelector('.e-chipcontent, .e-chips-content');
    return (content && content.textContent ? content.textContent : '').trim();
  };

  const getSelectedLabelsFromChips = (wrapper) => {
    if (!wrapper) return [];
    return Array.from(wrapper.querySelectorAll('.e-chips'))
      .map((chip) => getChipLabel(chip))
      .filter(Boolean);
  };

  const resolveSelectedLabels = (args) => {
    const fromChips = getSelectedLabelsFromChips(getWrapper());
    if (fromChips.length) return fromChips;

    const instance = getInstance();
    if (instance && instance.text) {
      const delim = instance.delimiterChar || ',';
      const fromText = String(instance.text)
        .split(delim)
        .map((entry) => entry.trim())
        .filter(Boolean);
      if (fromText.length) return fromText;
    }

    if (Array.isArray(args && args.value)) {
      return args.value.map((entry) => String(entry || '').trim()).filter(Boolean);
    }
    return [];
  };

  const buildSelectionSummaryText = (labels) =>
    labels && labels.length
      ? `${labels.length} selected: ${labels.join(', ')}.`
      : 'No items selected.';

  const resolveFieldLabel = () => {
    // Prefer the visible <label> text so announcements match what sighted
    // users see (e.g. "Keywords (Skills and Experience)" not a short alias).
    if (labelId) {
      const labelEl = document.getElementById(labelId);
      if (labelEl && labelEl.textContent) {
        const fromDom = labelEl.textContent.replace(/^\s*\*\s*/, '').trim();
        if (fromDom) return fromDom;
      }
    }
    if (fieldLabel) return fieldLabel;
    return 'items';
  };

  /**
   * Replace Syncfusion's unlabeled <span class="e-chips-close"> with a real
   * <button>, preserving classes/styles so existing CSS keeps working.
   * tabindex="-1" keeps Tab on the combobox; arrow keys still select chips.
   */
  const promoteCloseControlToButton = (el, ariaLabel) => {
    if (!el || !ariaLabel) return null;

    if (el.tagName === 'BUTTON') {
      el.setAttribute('type', 'button');
      el.setAttribute('aria-label', ariaLabel);
      el.setAttribute('tabindex', '-1');
      el.removeAttribute('aria-hidden');
      return el;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = el.className;
    button.setAttribute('aria-label', ariaLabel);
    button.setAttribute('tabindex', '-1');
    const style = el.getAttribute('style');
    if (style) {
      button.setAttribute('style', style);
    }

    if (el.parentNode) {
      el.parentNode.replaceChild(button, el);
    }
    return button;
  };

  const fixCloseButtons = () => {
    const wrapper = getWrapper();
    if (!wrapper) return;

    const instance = getInstance();
    const resolvedLabel = resolveFieldLabel();

    // Per-chip remove controls (inside each .e-chips chip). Include the field
    // label so focusing the button still announces which Multiselect it belongs to.
    Array.from(wrapper.querySelectorAll('.e-chips .e-chips-close')).forEach((closeEl) => {
      const chip = closeEl.closest('.e-chips');
      const chipLabel = getChipLabel(chip) || 'item';
      const wasSpan = closeEl.tagName !== 'BUTTON';
      const button = promoteCloseControlToButton(
        closeEl,
        `Remove ${chipLabel} from ${resolvedLabel}`
      );
      // Syncfusion bound mousedown on the original span; re-wire after replace.
      if (wasSpan && button && instance && typeof instance.onChipRemove === 'function') {
        button.addEventListener('mousedown', (e) => instance.onChipRemove(e));
      }
    });

    // Clear-all control (.e-close-hooker). Syncfusion keeps a reference on the
    // component instance (overAllClear), so update that after replacing.
    const clearAll = wrapper.querySelector('.e-chips-close.e-close-hooker, .e-close-hooker');
    if (clearAll) {
      const wasSpan = clearAll.tagName !== 'BUTTON';
      const button = promoteCloseControlToButton(
        clearAll,
        `Remove all selected ${resolvedLabel}`
      );
      if (button && instance) {
        instance.overAllClear = button;
        if (wasSpan && typeof instance.clearAll === 'function') {
          button.addEventListener('mousedown', (e) => instance.clearAll(e));
        }
      }
    }
  };

  const ensureLiveRegion = () => {
    const liveId = getLiveRegionId();
    if (!liveId) return null;

    let live = document.getElementById(liveId);
    if (!live) {
      live = document.createElement('span');
      live.id = liveId;
      live.className = 'sr-only';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'assertive');
      live.setAttribute('aria-atomic', 'true');
      const wrapper = getWrapper();
      const host = (wrapper && (wrapper.closest('.form-group') || wrapper.parentElement)) || document.body;
      host.appendChild(live);
    }
    return live;
  };

  const announceSelectionChange = (message) => {
    if (!message) return;
    const live = ensureLiveRegion();
    if (!live) return;
    // Clear then set so identical consecutive messages are still announced.
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = message;
    });
  };

  const announceDiff = (prevLabels, nextLabels) => {
    const prev = prevLabels || [];
    const next = nextLabels || [];
    const added = next.filter((label) => !prev.includes(label));
    const removed = prev.filter((label) => !next.includes(label));

    if (!added.length && !removed.length) return;

    if (next.length === 0 && prev.length > 1) {
      announceSelectionChange(`All ${resolveFieldLabel()} removed.`);
      return;
    }
    if (added.length === 1 && removed.length === 0) {
      announceSelectionChange(`${added[0]} selected.`);
      return;
    }
    if (removed.length === 1 && added.length === 0) {
      announceSelectionChange(`${removed[0]} removed.`);
      return;
    }
    announceSelectionChange(
      next.length
        ? `${next.length} selected: ${next.join(', ')}.`
        : `All ${resolveFieldLabel()} removed.`
    );
  };

  const updateSelectionSummary = () => {
    const summaryId = getSelectionSummaryId();
    if (!summaryId) return null;

    const wrapper = getWrapper();
    let summary = document.getElementById(summaryId);
    if (!summary) {
      summary = document.createElement('span');
      summary.id = summaryId;
      summary.className = 'sr-only';
      // Description only — spoken on focus via aria-describedby, not as a live region.
      const host = (wrapper && (wrapper.closest('.form-group') || wrapper.parentElement)) || document.body;
      host.appendChild(summary);
    }

    const labels = getSelectedLabelsFromChips(wrapper);
    const nextText = buildSelectionSummaryText(labels);

    if (summary.textContent !== nextText) {
      summary.textContent = nextText;
    }
    return summaryId;
  };

  const deferChipFixes = () => {
    setTimeout(() => {
      fixCloseButtons();
    }, 0);
  };

  const ensureChipObserver = () => {
    const wrapper = getWrapper();
    const chips = wrapper && wrapper.querySelector('.e-chips-collection');
    if (!chips || chipObserver) return;

    chipObserver = new MutationObserver(() => {
      updateSelectionSummary();
      deferChipFixes();
    });
    chipObserver.observe(chips, { childList: true, subtree: true, characterData: true });
  };

  // Clear-all icon lives outside the chip collection; watch the wrapper so we
  // still promote it when Syncfusion inserts/toggles it.
  const ensureClearObserver = () => {
    const wrapper = getWrapper();
    const multiSelectWrapper = wrapper && wrapper.querySelector('.e-multi-select-wrapper');
    if (!multiSelectWrapper || clearObserver) return;

    clearObserver = new MutationObserver(() => {
      if (isPopupOpen) {
        deferChipFixes();
        return;
      }
      fixCloseButtons();
    });
    clearObserver.observe(multiSelectWrapper, { childList: true, subtree: true });
  };

  const ensureDescribedByObserver = () => {
    const combobox = getCombobox();
    const summaryId = getSelectionSummaryId();
    if (!combobox || !summaryId || describedByObserver) return;

    describedByObserver = new MutationObserver(() => {
      // Leave native ARIA alone while the listbox is open so JAWS can speak
      // aria-selected. Also skip while a chip is focused for left/right nav.
      if (isPopupOpen) return;
      const wrapper = getWrapper();
      if (wrapper && wrapper.querySelector('.e-chips.e-chip-selected')) {
        return;
      }

      const current = combobox.getAttribute('aria-describedby') || '';
      if (!current.includes(summaryId) || (instructionsId && !current.includes(instructionsId))) {
        fixCombobox();
      }
    });
    describedByObserver.observe(combobox, {
      attributes: true,
      attributeFilter: ['aria-describedby'],
    });
  };

  const fixCombobox = () => {
    const combobox = getCombobox();
    if (!combobox) return;

    // Syncfusion defaults the accessible name to "multiselect"; drop it so the
    // visible field label (via aria-labelledby) provides the name instead.
    combobox.removeAttribute('aria-label');

    if (labelId) {
      combobox.setAttribute('aria-labelledby', labelId);
    }

    // Chip collection's aria-label="multiselect" blocks reading chip text when it
    // is used as a description. Strip it so leftover references are less harmful.
    const wrapper = getWrapper();
    const chipCollection = wrapper && wrapper.querySelector('.e-chips-collection');
    if (chipCollection) {
      chipCollection.removeAttribute('aria-label');
    }

    const summaryId = updateSelectionSummary();
    const chipId = chipCollection && chipCollection.id ? chipCollection.id : null;
    const liveId = getLiveRegionId();

    const existing = (combobox.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(Boolean);

    // Prefer our selection summary over Syncfusion's chip collection for
    // announcing current selections on focus. Keep other ids (e.g. field errors).
    // Never put the assertive live region in describedby.
    const ordered = [];
    if (summaryId) ordered.push(summaryId);

    existing.forEach((id) => {
      if (id === summaryId || id === instructionsId || id === chipId || id === liveId) return;
      if (!ordered.includes(id)) ordered.push(id);
    });

    if (instructionsId) ordered.push(instructionsId);

    if (ordered.length) {
      combobox.setAttribute('aria-describedby', ordered.join(' '));
    }

    previousLabels = getSelectedLabelsFromChips(wrapper);
    fixCloseButtons();
    ensureChipObserver();
    ensureClearObserver();
    ensureDescribedByObserver();
    patchInitialFocusAnnouncement();
  };

  /**
   * Syncfusion's own `announceInitialFocusInstruction` fires every time the
   * widget is focused (the flag it guards on is reset on blur) and only ever
   * speaks the generic keyboard-usage hint via `instance.chipAnnouncerLiveRegion`.
   * We override that single instance method so the same announcement also
   * states how many items are selected and names them, matching how
   * Syncfusion's own add/remove-chip announcements already behave.
   */
  const patchInitialFocusAnnouncement = () => {
    const instance = getInstance();
    if (!instance || instance.__a11yInitialFocusPatched) return;
    if (typeof instance.announceInitialFocusInstruction !== 'function') return;

    instance.__a11yInitialFocusPatched = true;
    instance.announceInitialFocusInstruction = function announceInitialFocusInstructionWithSelection() {
      if (!this.chipAnnouncerLiveRegion) return;
      const baseInstruction =
        'Use arrow keys to open popup and navigate items, Enter to select, and Backspace to delete selections';
      this.chipAnnouncerLiveRegion.textContent = '';
      setTimeout(() => {
        if (!this.chipAnnouncerLiveRegion) return;
        // Syncfusion's live-region instruction often replaces the normal
        // accessible-name speech on focus, so include the field label here.
        const label = resolveFieldLabel();
        const summary = buildSelectionSummaryText(getSelectedLabelsFromChips(getWrapper()));
        this.chipAnnouncerLiveRegion.textContent = `${label}. ${summary} ${baseInstruction}.`;
      }, 200);
    };
  };

  const fixListbox = (popupElement) => {
    if (!listboxLabel) return;
    const root = popupElement || document;
    const listbox = root.querySelector('[role="listbox"]');
    if (listbox) {
      listbox.setAttribute('aria-label', listboxLabel);
    }
  };

  return {
    created: () => {
      // Defer so the fix runs after Syncfusion finishes wiring its own ARIA.
      setTimeout(fixCombobox, 0);
    },
    focus: () => {
      fixCombobox();
      // Syncfusion may reset aria-describedby during focusInHandler; re-apply.
      setTimeout(fixCombobox, 0);
      setTimeout(fixCombobox, 250);
    },
    open: (args) => {
      isPopupOpen = true;
      // Only name the popup listbox. Do not rewrite combobox ARIA while JAWS
      // is interacting with native aria-selected options.
      fixListbox(args && args.popup && args.popup.element);
    },
    close: () => {
      isPopupOpen = false;
      setTimeout(fixCombobox, 0);
    },
    change: (args) => {
      const nextLabels = resolveSelectedLabels(args);
      announceDiff(previousLabels, nextLabels);
      previousLabels = nextLabels;
      updateSelectionSummary();
      // Defer chip-button promotion / combobox ARIA so JAWS can finish speaking
      // Syncfusion's native "selected" state first.
      setTimeout(() => {
        if (isPopupOpen) {
          fixCloseButtons();
        } else {
          fixCombobox();
        }
      }, 0);
      if (typeof onChange === 'function') {
        onChange(args);
      }
    },
  };
}
